import React, { useEffect, useState } from 'react';
import { Product } from 'constants/interface';
import { useDispatch, useSelector } from 'hooks/store';
import {
  getCultureProductsAction,
  isLoadingProductsSelector,
  productsEntitiesSelector,
} from 'state/slices/products';
import { Select, Spinner } from 'UI';
import { FormItemProps } from 'antd';
import { ProductTypeEnum } from 'constants/product';
import { useProducts } from 'hooks/products';

import {
  RemoteSelectContent,
  RemoteSelectSpinnerContainer,
} from './style';

interface CultureProductsSelectProps {
  cultureId?: string
  onSelect?: (product?: Product) => void
  onClear?: () => void
  form?: {
    FormItem: React.ComponentType<FormItemProps>
    formItemProps: FormItemProps
  }
  autoFetch?: boolean
  filter?: {
    productType?: ProductTypeEnum,
  },
  disabled?: boolean
  initialSelected?: Product
}

const CultureProductSelect = ({
  cultureId,
  onSelect = () => {},
  form,
  autoFetch,
  filter,
  disabled,
  initialSelected,
}: CultureProductsSelectProps): JSX.Element => {
  const dispatch = useDispatch();

  const productsEntities = useSelector(productsEntitiesSelector);
  const isLoadingProducts = useSelector(isLoadingProductsSelector);

  const productFilter = filter ? {
    types: filter.productType ? [filter.productType] : undefined,
  } : undefined;
  const products = useProducts(productFilter);

  const varietyMapper: Record<string, Product> = {};

  const [isFetchTriggered, setFetchTriggered] = useState(false);
  const [isDropdownOpened, setDropdownOpened] = useState<boolean>(false);

  const [selectedProduct, setSelectedProduct] = useState<Product | undefined>(initialSelected);

  const initialSelectedId = initialSelected?.id;

  useEffect(() => {
    if (initialSelectedId) {
      setSelectedProduct(productsEntities[initialSelectedId]);
    }
  }, [initialSelectedId]);

  const fetch = (cultureIdToFetch: string) => {
    setFetchTriggered(true);
    // TODO: fix, this currently is causing setstate on unmounted component (browser warning)
    dispatch(getCultureProductsAction({
      cultureId: cultureIdToFetch,
      filterData: {
        isPaginated: false,
        isActive: true,
      },
    }));
  };

  useEffect(() => {
    if (cultureId && !isFetchTriggered) {
      if (autoFetch) {
        fetch(cultureId);
      } else if (isDropdownOpened) {
        fetch(cultureId);
      }
    }
  }, [
    cultureId,
    autoFetch,
    isDropdownOpened,
    isFetchTriggered,
  ]);

  useEffect(() => {
    setFetchTriggered(false);
  }, [cultureId]);

  const selectedProductOption = selectedProduct
    ? [selectedProduct]
    : [];

  const selectItems = isDropdownOpened
    ? products
    : selectedProductOption;

  selectItems.forEach((product) => {
    if (product.variety.culture.id === cultureId) {
      varietyMapper[product.variety.id] = product;
    }
  });

  const selectElement = (
    <Select
      placeholder="Selecione uma variedade"
      disabled={(!cultureId) || disabled}
      loading={isLoadingProducts}
      showArrow={false}
      onDropdownVisibleChange={(isOpen) => setDropdownOpened(isOpen)}
      onSelect={(productId) => {
        const curSelectedProduct = productsEntities[productId];
        if (curSelectedProduct) {
          setSelectedProduct(curSelectedProduct);
          onSelect(productsEntities[productId]);
        }
      }}
      notFoundContent={<div>Nenhuma variedade disponível</div>}
      dropdownRender={(menu) => (
        <div>
          <RemoteSelectContent>
            {/* {menu} */}
            {(isLoadingProducts) ? (
              <RemoteSelectSpinnerContainer>
                <Spinner />
              </RemoteSelectSpinnerContainer>
            ) : menu}
          </RemoteSelectContent>
        </div>
      )}
    >
      {Object.values(varietyMapper).map((product) => (
        <Select.Option
          value={product.id}
          key={product.id}
        >
          {product.variety.name}
        </Select.Option>
      ))}
    </Select>
  );

  return (
    form ? (
      <form.FormItem
        {...form.formItemProps}
      >
        { selectElement }
      </form.FormItem>
    ) : selectElement
  );
};

export default CultureProductSelect;
